import posthog from "posthog-js";

import { INTERNAL_EMAIL_PATTERN, checkMeasurementCookieConsent, getEnvironment, isImpersonating } from "utils";

import { COUNTRY_CODE } from "consts";

import { User } from "models";

// 🚨TODO : The tracking plan's approved events. An unplanned name fails type-check, so add one here only once its row is Approved.
export type ProductAnalyticsEvent =
  | "signup_flow:signup_click"
  | "signup_flow:form_submit"
  | "signup_flow:signup_complete"
  | "signup_flow:login_click"
  | "signup_flow:login_submit"
  | "soilsights_onboarding:landing_view"
  | "soilsights_onboarding:flow_start"
  | "soilsights_onboarding:step_view"
  | "soilsights_onboarding:baseline_submit"
  | "soilsights_onboarding:baseline_validation_fail"
  | "soilsights_onboarding:forecast_generate"
  | "soilsights_onboarding:forecast_fail"
  | "soilsights_onboarding:practice_update"
  | "soilsights_onboarding:flow_complete"
  | "soilsights_onboarding:flow_cancel"
  | "soilsights:tab_view"
  | "soilsights:forecast_generate"
  | "soilsights:forecast_fail"
  | "soilsights:scenario_create"
  | "soilsights:scenario_update"
  | "soilsights:scenario_delete"
  | "soilsights:scenario_compare"
  | "soilsights:settings_update"
  | "soilsights:chart_view"
  | "soilsights:cover_crop_recommendation_view"
  | "soilsights:cover_crop_mix_add"
  | "soilsights:key_insights_view"
  | "soilsights:report_print"
  | "soilsights:explainer_view"
  | "soilsights:project_create"
  | "soilsights:project_delete"
  | "soilsights:tour_start"
  | "soilsights:tour_end";

// Global components take these, so each team passes its own event and never edits the component.
export type ProductEventProps = {
  productEvent?: ProductAnalyticsEvent;
  productEventProperties?: Record<string, unknown>;
};

// Unset, posthog-js falls back to the US cloud, where our EU key fails.
const POSTHOG_HOST = "https://eu.i.posthog.com";

// Word chars only, so masking a serialized JSON string can never eat a quote or an escape.
const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(\.[\w-]+)+/g;

// PostHog filters passwords and card numbers out of the box, but not emails.
export const maskEmails = (text: string) => text.replace(EMAIL_PATTERN, (email) => "*".repeat(email.length));

// The plan's module values and the screens in each. A screen in none of them sends no module.
const MODULE_SCREENS: Record<string, string[]> = {
  signup_flow: ["sign-up", "confirm-account"],
  sign_in: ["sign-in", "password", "password-reset"],
  home: ["home"],
  // Before field_management, since CMC also opens under all-fields.
  cmc: ["cmc", "all-fields/cmc"],
  field_management: ["all-fields", "farm", "historical-data"],
  footprinting: ["footprinting"],
  wallet: ["wallet", "banking-details", "credit-note"],
  soilsights: ["soilsight", "scenarios", "scenarios-landing-page", "scenarios-onboarding", "scenarios-next-steps", "scenarios-pdf"],
};

// We keep lowercase words only. Ids, tokens and emails in the path never reach PostHog.
const getSourceScreen = (pathname: string) =>
  pathname
    .split("/")
    .filter((segment) => /^[a-z][a-z-]*$/.test(segment))
    .join("/");

const getModule = (screen: string) =>
  Object.keys(MODULE_SCREENS).find((module) => MODULE_SCREENS[module].some((path) => screen === path || screen.startsWith(`${path}/`)));

const getMarket = (country: string | null) => (country === COUNTRY_CODE.UNITED_KINGDOM ? "uk" : country?.toLowerCase());

// Lower bound inclusive, largest first, as the plan cuts them.
const FARM_SIZE_BANDS: [number, string][] = [
  [1000, "ha_1000_plus"],
  [250, "ha_250_1000"],
  [100, "ha_100_250"],
  [50, "ha_50_100"],
  [20, "ha_20_50"],
  [0, "ha_0_20"],
];

const getFarmSizeBand = (hectares: number) => FARM_SIZE_BANDS.find(([lowerBound]) => hectares >= lowerBound)?.[1];

// isOffsettingFarm is also true with no program. We check the program name first.
const getProgramModel = (farm: User["farm"]) => {
  if (!farm.programName) return "none";
  return farm.isOffsettingFarm ? "offsetting" : "insetting";
};

const getFarmProperties = (farm: User["farm"]) => ({
  market: getMarket(farm.country),
  region: farm.region,
  farm_size_ha: farm.fieldArea,
  farm_size_band: getFarmSizeBand(farm.fieldArea),
  enrolled_ha: farm.programName ? farm.hectaresInConversion : 0,
  has_program: !!farm.programName,
  program_name: farm.programName,
  program_model: getProgramModel(farm),
  partner: farm.partner,
  program_start_date: farm.programStartDate,
});

// Lazy, so whichever call lands first after consent (boot, identify, a replayed SoilSight event) starts the client.
const getPostHog = () => {
  const env = getEnvironment();
  const isProductionTraffic = env.production || env.app;
  // We pick the key by host, not per build. build:app uses the local .env, so a per-build key could send app traffic to the dev project.
  const key = isProductionTraffic ? process.env.REACT_APP_POSTHOG_KEY_PROD : process.env.REACT_APP_POSTHOG_KEY_DEV;
  if (!key || env.admin || isImpersonating() || !checkMeasurementCookieConsent()) return null;

  if (!posthog.__loaded) {
    posthog.init(key, {
      api_host: POSTHOG_HOST,
      capture_pageview: "history_change",
      person_profiles: "identified_only",
      // Replay is switched on and scoped in the PostHog project settings (Session replay), not here.
      // Set here, it wins over the project's "Privacy and masking" setting, so nobody can unmask inputs from the PostHog UI.
      session_recording: { maskAllInputs: true, maskTextSelector: "*", maskTextFn: maskEmails },
      // Impersonation can start after boot. This also keeps autocapture and replay off the farmer's person.
      before_send: (event) => {
        if (!event || isImpersonating()) return null;
        // Replay text is already masked above, and re-serializing every snapshot batch is costly.
        if (event.event !== "$snapshot") event.properties = JSON.parse(maskEmails(JSON.stringify(event.properties)));
        return event;
      },
    });
  }
  return posthog;
};

export const productAnalytics = {
  init: () => {
    getPostHog();
  },

  track: (event: ProductAnalyticsEvent, properties?: Record<string, unknown>) => {
    const client = getPostHog();
    if (!client) return;

    const sourceScreen = getSourceScreen(window.location.pathname);
    // We bump this when the SoilSights flow changes, so funnels can split by version.
    const flowVersion = event.startsWith("soilsights") ? { flow_version: "v1" } : {};
    client.capture(event, { module: getModule(sourceScreen), source_screen: sourceScreen, ...flowVersion, ...properties });
  },

  // Our user id, never the email, so the API's server-side events land on the same person.
  identify: (user: User) => {
    const client = getPostHog();
    if (!client) return;

    client.identify(
      user.id,
      { language: user.language, is_internal: INTERNAL_EMAIL_PATTERN.test(user.email ?? "") },
      { registration_date: user.registrationDate }
    );
    if (user.farm?.id) client.group("farm", user.farm.id, getFarmProperties(user.farm));
    // Test is a farm fact, not a person fact. We register it on each event
    client.register({ is_test_farm: !!user.farm?.isTest });
  },

  group: (farmId: string) => {
    getPostHog()?.group("farm", farmId);
  },

  // First write wins for the session. posthog.reset() on sign-out clears it, so the next user in the tab starts fresh.
  setSessionPropertyOnce: (key: string, value: unknown) => {
    const client = getPostHog();
    if (!client || client.getSessionProperty(key) !== undefined) return;
    client.register_for_session({ [key]: value });
  },

  reset: () => {
    if (posthog.__loaded) posthog.reset();
  },
};
